<template>
	<b-dropdown
		@show="dropdownActive = true"
		@hide="dropdownActive = false"
		:text="selectedRole"
		class="form-control"
		block
		lazy
	>
		<slide-x-left-transition origin="left" group>
			<template v-if="'list' === activeTab">
				<div key="tab-list">
					<b-dropdown-text class="text-right">
						<base-button
							@click.prevent.stop="activeTab = 'form'"
							type="primary"
							size="sm"
							class="m-0 mb-2 py-2 px-3 text-capitalize"
							>Add New</base-button
						>
						<b-form-input
							@input="onSearch"
							size="sm"
							placeholder="Search role"
						></b-form-input>
					</b-dropdown-text>
					<b-dropdown-divider></b-dropdown-divider>
					<template v-if="0 < rolesList.length">
						<b-dropdown-item-button
							v-for="roleItem in rolesList"
							@click.prevent="$emit('input', roleItem.value)"
							:key="`option-${roleItem.value}`"
							:active="roleItem.value == value"
							active-class="bg-light"
							button-class="p-0"
						>
							<b-overlay
								:show="activeConfirmation == roleItem.value"
								opacity="0.8"
								variant="white"
								class="w-100 py-2 px-3 d-flex justify-content-between"
							>
								<template #overlay>
									<div
										class="w-100 d-flex justify-content-between"
									>
										<span class="ml-auto">
											<b-link
												@click.prevent.stop="
													confirmRemove
												"
												class="mx-1 rounded-circle text-white bg-primary"
											>
												<b-icon icon="check"></b-icon>
											</b-link>
											<b-link
												@click.prevent.stop="
													activeConfirmation = null
												"
												class="mx-1 rounded-circle text-white bg-danger"
											>
												<b-icon icon="x"></b-icon>
											</b-link>
										</span>
									</div>
								</template>
								{{ roleItem.text }}
								<span>
									<b-link
										@click.prevent.stop="editRole(roleItem)"
										class="text-primary"
									>
										<b-icon
											icon="pencil"
											font-scale="0.975"
										></b-icon>
									</b-link>
									<b-link
										@click.prevent.stop="
											removeRole(roleItem)
										"
										class="ml-2 text-danger"
									>
										<b-icon
											icon="trash"
											font-scale="0.975"
										></b-icon>
									</b-link>
								</span>
							</b-overlay>
						</b-dropdown-item-button>
					</template>
					<small v-else class="d-block py-2 px-3"
						>No roles found.</small
					>
				</div>
			</template>
			<template v-else>
				<validation-observer v-slot="{ invalid }" key="tab-form">
					<b-dropdown-form @submit.prevent.stop="onSubmit">
						<h4 class="mb-3">
							{{ formTitle }}
						</h4>

						<b-dropdown-divider></b-dropdown-divider>

						<base-input
							v-model="models.role"
							rules="required"
							name="Role"
							label="Role"
						></base-input>

						<div class="d-flex justify-content-between">
							<base-button
								@click.prevent.stop="activeTab = 'list'"
								type="white"
								class="px-0 bg-transparent shadow-none"
								>Cancel</base-button
							>
							<base-button
								:disabled="invalid"
								:loading="formProcessing"
								native-type="submit"
								type="primary"
								size="sm"
								class="py-2 px-3"
								>Save</base-button
							>
						</div>
					</b-dropdown-form>
				</validation-observer>
			</template>
		</slide-x-left-transition>
	</b-dropdown>
</template>

<script>
import Fuse from "fuse.js";
import { debounce, orderBy } from "lodash-es";
import { ErrorService, RolesService } from "@services";

import { SlideXLeftTransition } from "vue2-transitions";

export default {
	name: "form-role-dropdown",
	components: {
		SlideXLeftTransition,
	},
	props: {
		value: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			dropdownActive: false,
			formProcessing: false,
			activeConfirmation: null,
			confirmationAction: null,
			models: {},
			activeTab: "list",
			searchQuery: "",
		};
	},
	computed: {
		rolesService() {
			return RolesService;
		},
		formTitle() {
			return `${RolesService.role.id ? "Update" : "Create"} Role`;
		},
		selectedRole() {
			if (!this.rolesList.length) return "Select Role";

			const roleIndex = this.rolesList.findIndex(
				(item) => item.value == this.value
			);

			return 0 <= roleIndex
				? this.rolesList[roleIndex].text
				: "Select Role";
		},
		rolesList() {
			var rolesList = RolesService.roles.results;

			if (this.searchQuery) {
				const fuse = new Fuse(rolesList, {
					keys: ["role"],
					includeScore: true,
					useExtendedSearch: true,
					threshold: 0.3,
				});

				rolesList = fuse
					.search(this.searchQuery)
					.map((item) => item.item);
			}

			rolesList = rolesList.map((item) => {
				return {
					value: item.id,
					text: item.role,
				};
			});

			return orderBy(rolesList, "text", "asc");
		},
	},
	watch: {
		"rolesService.role": {
			deep: true,
			immediate: true,
			handler(newValue) {
				if (!newValue) return;

				this.models = {
					id: newValue.id,
					role: newValue.role,
				};
			},
		},
		dropdownActive(newValue) {
			if (!newValue) this.searchQuery = "";
		},
		activeTab(newValue) {
			if ("form" !== newValue) RolesService.set("role", null);
		},
	},
	methods: {
		onSearch: debounce(function (keyword) {
			this.searchQuery = keyword;
		}, 500),
		editRole(option) {
			RolesService.role.id = option.value;
			RolesService.role.role = option.text;

			this.activeTab = "form";
		},
		removeRole(option) {
			this.activeConfirmation = option.value;
			this.confirmationAction = "delete";
		},
		async onSubmit() {
			var response;

			this.formProcessing = true;

			if (RolesService.role.id)
				response = await RolesService.update(this.models);
			else response = await RolesService.create(this.models);

			this.$toasted.show(response.data.data.message);

			RolesService.set("role", null);

			this.formProcessing = false;
			this.activeTab = "list";
		},
		async confirmRemove() {
			if (!this.activeConfirmation) return false;

			if ("delete" == this.confirmationAction) {
				const response = await RolesService.delete({
					id: this.activeConfirmation,
				});

				this.$toasted.show(response.data.data.message);

				this.activeConfirmation = null;
				this.confirmationAction = null;
			}
		},
	},
	async mounted() {
		RolesService.set("list", null);
		await RolesService.list({
			page_size: 1000,
		});

		ErrorService.stopProcessingOnFail(this);
	},
};
</script>

<style lang="scss">
.dropdown-menu {
	width: 100%;
	max-height: 265px;
	overflow-y: auto;

	.b-overlay .rounded-circle {
		width: 20px;
		height: 20px;
		line-height: 20px;
		display: inline-block;
	}
}
</style>
